import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { api } from '../api/client';
import { usePlans, useRouters } from '../api/queries';
import { useCurrentRouter } from '../lib/current-router';
import type { HotspotUserDto, HotspotUserStatus, Paginated } from '../api/types';
import { useAuth } from '../auth/auth-context';
import { HotspotUserStatusBadge } from '../components/status';
import {
  Card,
  ConfirmDialog,
  CopyButton,
  Empty,
  ErrorAlert,
  Field,
  Modal,
  Pagination,
  Spinner,
} from '../components/ui';
import { formatBytes, formatDateTime, formatDuration, formatRelative, formatRemainingSeconds, fromDateTimeInput } from '../lib/format';

const STATUS_OPTIONS: { value: HotspotUserStatus | ''; label: string }[] = [
  { value: '', label: 'Todos los estados' },
  { value: 'ACTIVE', label: 'Activos' },
  { value: 'PENDING_SYNC', label: 'Pendientes en router' },
  { value: 'DISABLED', label: 'Bloqueados' },
  { value: 'EXPIRED', label: 'Caducados' },
  { value: 'DELETED', label: 'Eliminados' },
];

export function HotspotUsersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { canOnRouter } = useAuth();
  const queryClient = useQueryClient();
  const current = useCurrentRouter();

  const routerId = current.routerId;
  const planId = searchParams.get('planId') ?? '';
  const status = (searchParams.get('status') ?? '') as HotspotUserStatus | '';
  const search = searchParams.get('search') ?? '';
  const page = Number(searchParams.get('page') ?? '1');

  const routers = useRouters();
  const plans = usePlans(routerId || undefined, false, 'HOTSPOT');

  // El filtro de plan es de un router concreto: al cambiar de router deja de tener sentido.
  const previousRouterId = useRef(routerId);
  useEffect(() => {
    if (previousRouterId.current === routerId) return;
    previousRouterId.current = routerId;
    const next = new URLSearchParams(searchParams);
    next.delete('planId');
    next.delete('page');
    setSearchParams(next, { replace: true });
  }, [routerId, searchParams, setSearchParams]);

  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<HotspotUserDto | null>(null);
  const [deleting, setDeleting] = useState<HotspotUserDto | null>(null);
  const [renewing, setRenewing] = useState<HotspotUserDto | null>(null);
  const [revealed, setRevealed] = useState<HotspotUserDto | null>(null);
  const [pageError, setPageError] = useState<unknown>(null);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    // Cambiar cualquier filtro invalida la página actual.
    if (key !== 'page') next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const users = useQuery({
    queryKey: ['hotspot-users', { routerId, planId, status, search, page }],
    enabled: Boolean(routerId),
    queryFn: () =>
      api.get<Paginated<HotspotUserDto>>('/hotspot-users', {
        routerId,
        planId: planId || undefined,
        status: status || undefined,
        search: search || undefined,
        // Sólo credenciales creadas a mano: los vouchers son otro producto y viven
        // en su propia pantalla, aunque compartan tabla en el router.
        kind: 'MANUAL',
        page,
        pageSize: 25,
      }),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['hotspot-users'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const changeStatus = useMutation({
    mutationFn: ({ user, next }: { user: HotspotUserDto; next: 'ACTIVE' | 'DISABLED' }) =>
      api.patch<HotspotUserDto>(`/hotspot-users/${user.id}/status`, { status: next }),
    onSuccess: invalidate,
    onError: (error) => setPageError(error),
  });

  const resetCounters = useMutation({
    mutationFn: (user: HotspotUserDto) => api.post<void>(`/hotspot-users/${user.id}/reset-counters`),
    onSuccess: invalidate,
    onError: (error) => setPageError(error),
  });

  const retrySync = useMutation({
    mutationFn: (user: HotspotUserDto) => api.post<HotspotUserDto>(`/hotspot-users/${user.id}/retry-sync`),
    onSuccess: invalidate,
    onError: (error) => setPageError(error),
  });

  const remove = useMutation({
    mutationFn: (user: HotspotUserDto) => api.del<void>(`/hotspot-users/${user.id}`),
    onSuccess: () => {
      setDeleting(null);
      invalidate();
    },
    onError: (error) => setPageError(error),
  });

  const reveal = useMutation({
    mutationFn: (user: HotspotUserDto) => api.get<HotspotUserDto>(`/hotspot-users/${user.id}`),
    onSuccess: (detail) => setRevealed(detail),
    onError: (error) => setPageError(error),
  });

  const canCreateSomewhere = (routers.data ?? []).some((router) => canOnRouter(router.id, 'create'));

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h1>Usuarios HotSpot</h1>
          <p>
            Credenciales del portal cautivo creadas una a una. Los códigos generados por lote
            están en «Vouchers».
          </p>
        </div>
        {canCreateSomewhere && (
          <button type="button" className="primary" onClick={() => setCreating(true)}>
            Crear usuario
          </button>
        )}
      </div>

      <ErrorAlert error={pageError} onDismiss={() => setPageError(null)} />

      <div className="toolbar">
        <select value={planId} onChange={(event) => setParam('planId', event.target.value)} style={{ width: 'auto' }}>
          <option value="">Todos los planes</option>
          {(plans.data ?? []).map((plan) => (
            <option key={plan.id} value={plan.id}>
              {plan.name}
            </option>
          ))}
        </select>

        <select value={status} onChange={(event) => setParam('status', event.target.value)} style={{ width: 'auto' }}>
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <input
          value={search}
          onChange={(event) => setParam('search', event.target.value)}
          placeholder="Buscar usuario o comentario…"
          style={{ width: 260 }}
        />

        {users.isFetching && <Spinner label="" />}
      </div>

      <Card
        footer={
          users.data && users.data.meta.total > 0 ? (
            <Pagination
              page={users.data.meta.page}
              totalPages={users.data.meta.totalPages}
              total={users.data.meta.total}
              onPage={(next) => setParam('page', String(next))}
            />
          ) : undefined
        }
      >
        {users.isLoading ? (
          <div className="card-body">
            <Spinner />
          </div>
        ) : users.data && users.data.data.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Plan</th>
                  <th>Estado</th>
                  <th className="num">Consumo</th>
                  <th>Caduca</th>
                  <th className="actions" />
                </tr>
              </thead>
              <tbody>
                {users.data.data.map((user) => {
                  const canWrite = canOnRouter(user.routerId, 'create');
                  const canDelete = canOnRouter(user.routerId, 'delete');

                  return (
                    <tr key={user.id}>
                      <td>
                        <strong className="mono">{user.username}</strong>
                        <div className="faint" style={{ fontSize: 12 }}>
                          {user.comment || `creado ${formatRelative(user.createdAt)}`}
                          {user.createdByName ? ` · por ${user.createdByName}` : ''}
                        </div>
                      </td>
                      <td>{user.planName ?? <span className="faint">—</span>}</td>
                      <td>
                        <HotspotUserStatusBadge status={user.status} timeConsumed={user.timeConsumed} />
                        {user.syncError && (
                          <div className="faint" style={{ fontSize: 12, maxWidth: 240 }}>
                            {user.syncError}
                          </div>
                        )}
                      </td>
                      <td className="num">
                        {formatBytes(user.bytesIn + user.bytesOut)}
                        <div className="faint" style={{ fontSize: 12 }}>
                          {formatDuration(user.uptimeSeconds)} usados
                        </div>
                      </td>
                      <td>{user.expiresAt ? formatRelative(user.expiresAt) : <span className="faint">—</span>}</td>
                      <td>
                        {user.planDurationMinutes ? <div className="faint" style={{ fontSize: 12 }}>uso: {formatRemainingSeconds(user.planDurationMinutes * 60 - user.uptimeSeconds)}</div> : null}
                        {user.planValidityMinutes ? <div className="faint" style={{ fontSize: 12 }}>validez: {user.firstUsedAt && user.expiresAt ? formatRemainingSeconds((new Date(user.expiresAt).getTime() - Date.now()) / 1000) : 'sin iniciar'}</div> : null}
                        {!user.planDurationMinutes && !user.planValidityMinutes ? <span className="faint">—</span> : null}
                      </td>
                      <td className="actions">
                        <button type="button" className="small ghost" onClick={() => reveal.mutate(user)}>
                          Ver
                        </button>

                        {canWrite && user.status === 'PENDING_SYNC' && (
                          <button
                            type="button"
                            className="small"
                            style={{ marginLeft: 4 }}
                            disabled={retrySync.isPending}
                            onClick={() => retrySync.mutate(user)}
                          >
                            Reintentar
                          </button>
                        )}

                        {canWrite && user.status === 'ACTIVE' && (
                          <button
                            type="button"
                            className="small"
                            style={{ marginLeft: 4 }}
                            onClick={() => changeStatus.mutate({ user, next: 'DISABLED' })}
                          >
                            Bloquear
                          </button>
                        )}

                        {canWrite && user.status === 'DISABLED' && (
                          <button
                            type="button"
                            className="small"
                            style={{ marginLeft: 4 }}
                            onClick={() => changeStatus.mutate({ user, next: 'ACTIVE' })}
                          >
                            Desbloquear
                          </button>
                        )}

                        {canWrite && user.status !== 'DELETED' && (
                          <button
                            type="button"
                            className="small"
                            style={{ marginLeft: 4 }}
                            onClick={() => setRenewing(user)}
                          >
                            Renovar
                          </button>
                        )}

                        {canWrite && user.status !== 'DELETED' && (
                          <button
                            type="button"
                            className="small ghost"
                            style={{ marginLeft: 4 }}
                            disabled={resetCounters.isPending}
                            onClick={() => resetCounters.mutate(user)}
                            title="Pone a cero el consumo y el tiempo usado en el router"
                          >
                            Contadores
                          </button>
                        )}

                        {canDelete && user.status !== 'DELETED' && (
                          <button
                            type="button"
                            className="small danger"
                            style={{ marginLeft: 4 }}
                            onClick={() => setDeleting(user)}
                          >
                            Eliminar
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty>No hay usuarios que coincidan con los filtros.</Empty>
        )}
      </Card>

      {creating && (
        <CreateUserModal
          onClose={() => setCreating(false)}
          onCreated={(user) => {
            setCreating(false);
            setCreated(user);
            invalidate();
          }}
        />
      )}

      {created && <CredentialModal user={created} onClose={() => setCreated(null)} isNew />}
      {revealed && <CredentialModal user={revealed} onClose={() => setRevealed(null)} />}

      {renewing && (
        <RenewModal
          user={renewing}
          onClose={() => setRenewing(null)}
          onDone={() => {
            setRenewing(null);
            invalidate();
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={`Eliminar ${deleting.username}`}
          danger
          busy={remove.isPending}
          confirmLabel="Eliminar del router"
          message={
            <p>
              Se borra la credencial del MikroTik y se corta su sesión si está conectado. La fila se
              conserva en MikroManager marcada como eliminada, para no perder el historial de
              consumo ni la auditoría.
            </p>
          }
          onCancel={() => setDeleting(null)}
          onConfirm={() => remove.mutate(deleting)}
        />
      )}
    </main>
  );
}

function CreateUserModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (user: HotspotUserDto) => void;
}) {
  const { canOnRouter } = useAuth();
  const routers = useRouters();
  const current = useCurrentRouter();
  const available = (routers.data ?? []).filter(
    (router) => router.isActive && canOnRouter(router.id, 'create'),
  );

  // Se abre sobre el router de trabajo para no crear por descuido en otro.
  const [routerId, setRouterId] = useState(() =>
    available.some((router) => router.id === current.routerId) ? current.routerId : '',
  );
  const [planId, setPlanId] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [comment, setComment] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState<unknown>(null);

  const plans = usePlans(routerId || undefined, false, 'HOTSPOT');

  const create = useMutation({
    mutationFn: () =>
      api.post<HotspotUserDto>('/hotspot-users', {
        routerId,
        planId,
        username: username || undefined,
        password: password || undefined,
        comment: comment || undefined,
        expiresAt: fromDateTimeInput(expiresAt),
      }),
    onSuccess: onCreated,
    onError: (caught) => setError(caught),
  });

  return (
    <Modal
      title="Crear usuario HotSpot"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="primary"
            disabled={create.isPending || !routerId || !planId}
            onClick={() => create.mutate()}
          >
            {create.isPending ? <Spinner label="Creando…" /> : 'Crear usuario'}
          </button>
        </>
      }
    >
      <ErrorAlert error={error} onDismiss={() => setError(null)} />

      {available.length === 0 ? (
        <p className="muted">
          No tienes permiso de creación en ningún router activo. Pide a un administrador que te
          asigne acceso.
        </p>
      ) : (
        <>
          <div className="form-row">
            <Field label="Router">
              <select
                value={routerId}
                onChange={(event) => {
                  setRouterId(event.target.value);
                  setPlanId('');
                }}
              >
                <option value="">Selecciona…</option>
                {available.map((router) => (
                  <option key={router.id} value={router.id}>
                    {router.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Plan">
              <select value={planId} onChange={(event) => setPlanId(event.target.value)} disabled={!routerId}>
                <option value="">Selecciona…</option>
                {(plans.data ?? []).map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="form-row">
            <Field label="Usuario" hint="Vacío = se genera automáticamente">
              <input value={username} onChange={(event) => setUsername(event.target.value)} />
            </Field>
            <Field label="Contraseña" hint="Vacío = se genera aleatoria">
              <input value={password} onChange={(event) => setPassword(event.target.value)} />
            </Field>
          </div>

          <Field label="Comentario">
            <input
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder="Nombre del cliente, mesa, habitación…"
            />
          </Field>

          <Field
            label="Caducidad por fecha"
            hint="Opcional. Independiente del tiempo de uso del plan: la credencial se bloquea llegada esa fecha aunque no se haya consumido."
          >
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(event) => setExpiresAt(event.target.value)}
            />
          </Field>
        </>
      )}
    </Modal>
  );
}

function CredentialModal({
  user,
  onClose,
  isNew,
}: {
  user: HotspotUserDto;
  onClose: () => void;
  isNew?: boolean;
}) {
  return (
    <Modal
      title={isNew ? 'Usuario creado' : user.username}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={() => window.print()}>
            Imprimir
          </button>
          <button type="button" className="primary" onClick={onClose}>
            Listo
          </button>
        </>
      }
    >
      {user.status === 'PENDING_SYNC' && (
        <div className="alert warn">
          <strong>Guardado en MikroManager, pero todavía no en el router.</strong>
          <div style={{ marginTop: 4 }}>
            La credencial no funcionará hasta que se sincronice. Usa «Reintentar» cuando el router
            vuelva a responder.
          </div>
          {user.syncError && <div className="detail">{user.syncError}</div>}
        </div>
      )}

      <div className="credential-box">
        <div>
          <div className="stat-label">Usuario</div>
          <div className="value">{user.username}</div>
        </div>
        <CopyButton value={user.username} />
      </div>

      {user.password ? (
        <div className="credential-box">
          <div>
            <div className="stat-label">Contraseña</div>
            <div className="value">{user.password}</div>
          </div>
          <CopyButton value={user.password} />
        </div>
      ) : (
        <p className="muted">
          No tienes permiso de creación en este router, así que la contraseña no se muestra.
        </p>
      )}

      <table style={{ marginTop: 14 }}>
        <tbody>
          <tr>
            <th>Plan</th>
            <td>{user.planName ?? '—'}</td>
          </tr>
          <tr>
            <th>Router</th>
            <td>{user.routerName ?? '—'}</td>
          </tr>
          <tr>
            <th>Consumo</th>
            <td>
              ↓ {formatBytes(user.bytesOut)} · ↑ {formatBytes(user.bytesIn)}
            </td>
          </tr>
          <tr>
            <th>Tiempo usado</th>
            <td>{formatDuration(user.uptimeSeconds)}</td>
          </tr>
          <tr>
            <th>Primer uso</th>
            <td>{formatDateTime(user.firstUsedAt)}</td>
          </tr>
          <tr>
            <th>Caduca</th>
            <td>{formatDateTime(user.expiresAt)}</td>
          </tr>
          {user.password && (
            <tr>
              <th>Copiar ambos</th>
              <td>
                <CopyButton value={`${user.username} / ${user.password}`} label="Usuario y contraseña" />
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </Modal>
  );
}

function RenewModal({
  user,
  onClose,
  onDone,
}: {
  user: HotspotUserDto;
  onClose: () => void;
  onDone: () => void;
}) {
  const plans = usePlans(user.routerId, false, 'HOTSPOT');
  const [planId, setPlanId] = useState(user.planId ?? '');
  const [expiresAt, setExpiresAt] = useState('');
  const [resetCounters, setResetCounters] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const renew = useMutation({
    mutationFn: () =>
      api.post<HotspotUserDto>(`/hotspot-users/${user.id}/renew`, {
        planId: planId || undefined,
        expiresAt: fromDateTimeInput(expiresAt),
        resetCounters,
      }),
    onSuccess: onDone,
    onError: (caught) => setError(caught),
  });

  return (
    <Modal
      title={`Renovar ${user.username}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="primary" disabled={renew.isPending} onClick={() => renew.mutate()}>
            {renew.isPending ? <Spinner label="Renovando…" /> : 'Renovar'}
          </button>
        </>
      }
    >
      <ErrorAlert error={error} onDismiss={() => setError(null)} />

      <Field label="Plan">
        <select value={planId} onChange={(event) => setPlanId(event.target.value)}>
          {/* Un plan global heredado ya no se lista, pero el usuario puede conservarlo. */}
          {user.planId && !(plans.data ?? []).some((plan) => plan.id === user.planId) && (
            <option value={user.planId}>{user.planName ?? 'Plan actual'} (actual)</option>
          )}
          {(plans.data ?? []).map((plan) => (
            <option key={plan.id} value={plan.id}>
              {plan.name}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Nueva caducidad" hint="Vacío = sin caducidad por fecha">
        <input
          type="datetime-local"
          value={expiresAt}
          onChange={(event) => setExpiresAt(event.target.value)}
        />
      </Field>

      <label className="inline">
        <input
          type="checkbox"
          checked={resetCounters}
          onChange={(event) => setResetCounters(event.target.checked)}
        />
        Poner los contadores a cero
      </label>
      <p className="faint" style={{ fontSize: 12 }}>
        Sin esto, el tiempo ya consumido sigue contando contra el límite del plan y la renovación no
        dará acceso.
      </p>
    </Modal>
  );
}
